import React from "react";
import { CiStreamOn } from "react-icons/ci";
import runTimeVars from "../runTimeVars";
import {
  FaChartLine,
  FaClock,
  FaCog,
  FaDatabase,
  FaHome,
  FaRegUser,
} from "react-icons/fa";
import {
  FaBoltLightning,
  FaCode,
  FaDownload,
  FaFlagCheckered,
  FaQuestion,
  FaRadio,
  FaServer,
  FaShield,
  FaTableList,
  FaTabletScreenButton,
} from "react-icons/fa6";
import { GiTyre, GiRabbit } from "react-icons/gi";
import { HiMiniTv } from "react-icons/hi2";
import { BsHeadsetVr } from "react-icons/bs";
import { TbHelmet } from "react-icons/tb";
import { PiStrategy } from "react-icons/pi";
import { CgPerformance } from "react-icons/cg";
import EnvisionSVG from "../assets/images/envision.svg";
import { SlGraph } from "react-icons/sl";
import { MdOutlineReplayCircleFilled } from "react-icons/md";

const disabledPages = runTimeVars.disabledPages.split(",");
const enableMsalAuth = runTimeVars.enableMsalAuth !== "false";

export type RouteConfig = {
  name: string;
  path: string;
  showInNavBar: boolean;
  requiresPermission: boolean;
  permissionGroup: string | null;
  permissionBit: number | null;
  enabled: boolean;
  icon?: React.ComponentType;
};

const EnvisionIcon = (props: React.ImgHTMLAttributes<HTMLImageElement>) =>
  React.createElement("img", {
    src: EnvisionSVG,
    alt: "Envision",
    style: {
      width: "13px",
      height: "13px",
      filter: "brightness(0) invert(1)",
    },
    ...props,
  });

export const roleIcons: Record<string, React.ComponentType> = {
  Administrator: FaShield,
  Developer: FaCode,
  StrategyEngineer: PiStrategy,
  PerformanceEngineer: CgPerformance,
  SystemsEngineer: FaCog,
  Driver: TbHelmet,
  BasicUser: FaRegUser,
  Envision: EnvisionIcon,
  ExternalUser: FaQuestion,
};

const rawRouteConfigs: Record<string, RouteConfig> = {
  "/": {
    name: "Home",
    path: "/",
    showInNavBar: true,
    requiresPermission: false,
    permissionGroup: null,
    permissionBit: null,
    enabled: true,
    icon: FaHome,
  },
  "/login": {
    name: "Login",
    path: "/login",
    showInNavBar: false,
    requiresPermission: false,
    permissionGroup: null,
    permissionBit: null,
    enabled: enableMsalAuth, // Only show login page if MSAL auth is enabled
  },
  "/timing": {
    name: "Timing",
    path: "/timing",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Timing",
    permissionBit: 0,
    enabled: !disabledPages.includes("/timing"),
    icon: FaClock,
  },
  "/loop-heatmap": {
    name: "Loop Heatmap",
    path: "/loop-heatmap",
    showInNavBar: false,
    requiresPermission: true,
    permissionGroup: "Timing",
    permissionBit: 1,
    enabled: !disabledPages.includes("/loop-heatmap"),
  },
  "/race": {
    name: "Race",
    path: "/race",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Race",
    permissionBit: 0,
    enabled: !disabledPages.includes("/race"),
    icon: FaFlagCheckered,
  },
  "/race-data": {
    name: "Race Data",
    path: "/race-data",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Race Data",
    permissionBit: 0,
    enabled: !disabledPages.includes("/race-data"),
    icon: FaTableList,
  },
  "/pit-boost": {
    name: "Pit Boost",
    path: "/pit-boost",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Pit Boost",
    permissionBit: 0,
    enabled: !disabledPages.includes("/pit-boost"),
    icon: FaBoltLightning,
  },
  "/pit-boost-leaderboard": {
    name: "Pit Boost Leaderboard",
    path: "/pit-boost-leaderboard",
    showInNavBar: false,
    requiresPermission: true,
    permissionGroup: "Pit Boost",
    permissionBit: 1,
    enabled: !disabledPages.includes("/pit-boost-leaderboard"),
  },
  "/fp-analysis": {
    name: "FP Analysis",
    path: "/fp-analysis",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "FP Analysis",
    permissionBit: 0,
    enabled: !disabledPages.includes("/fp-analysis"),
    icon: SlGraph,
  },
  "/wintax": {
    name: "WinTAX",
    path: "/wintax",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "WinTAX",
    permissionBit: 0,
    enabled: !disabledPages.includes("/wintax"),
    icon: FaTabletScreenButton,
  },
  "/wintax-summary": {
    name: "WinTAX Summary",
    path: "/wintax-summary",
    showInNavBar: false,
    requiresPermission: true,
    permissionGroup: "WinTAX",
    permissionBit: 1,
    enabled: !disabledPages.includes("/wintax-summary"),
  },
  "/tpms-jaguar": {
    name: "TPMS Jaguar",
    path: "/tpms-jaguar",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "TPMS Jaguar",
    permissionBit: 0,
    enabled: !disabledPages.includes("/tpms-jaguar"),
    icon: GiTyre,
  },
  "/tpms-summary": {
    name: "TPMS Summary",
    path: "/tpms-summary",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "TPMS Summary",
    permissionBit: 0,
    enabled: !disabledPages.includes("/tpms-summary"),
    icon: FaDatabase,
  },
  "/virtual-loops": {
    name: "Virtual Loops",
    path: "/virtual-loops",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Virtual Loops",
    permissionBit: 0,
    enabled: !disabledPages.includes("/virtual-loops"),
    icon: BsHeadsetVr,
  },
  "/tv-energy": {
    name: "TV Energy",
    path: "/tv-energy",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "TV Energy",
    permissionBit: 0,
    enabled: !disabledPages.includes("/tv-energy"),
    icon: HiMiniTv,
  },
  "/tv-energy-graph": {
    name: "TV Energy Graph",
    path: "/tv-energy-graph",
    showInNavBar: false,
    requiresPermission: true,
    permissionGroup: "TV Energy",
    permissionBit: 1,
    enabled: !disabledPages.includes("/tv-energy-graph"),
  },
  "/tv-energy-table": {
    name: "TV Energy Table",
    path: "/tv-energy-table",
    showInNavBar: false,
    requiresPermission: true,
    permissionGroup: "TV Energy",
    permissionBit: 2,
    enabled: !disabledPages.includes("/tv-energy-table"),
  },
  "/performance-metrics": {
    name: "Performance Metrics",
    path: "/performance-metrics",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Performance Metrics",
    permissionBit: 0,
    enabled: !disabledPages.includes("/performance-metrics"),
    icon: FaChartLine,
  },
  "/radio": {
    name: "Radio",
    path: "/radio",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Radio",
    permissionBit: 0,
    enabled: !disabledPages.includes("/radio"),
    icon: FaRadio,
  },
  "/ress": {
    name: "RESS",
    path: "/ress",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "RESS",
    permissionBit: 0,
    enabled: !disabledPages.includes("/ress"),
    icon: GiRabbit,
  },
  "/v2-live": {
    name: "V2 Live",
    path: "/v2-live",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "V2 Live",
    permissionBit: 0,
    enabled: !disabledPages.includes("/v2-live"),
    icon: CiStreamOn,
  },
  "/replay": {
    name: "Replay",
    path: "/replay",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Replay",
    permissionBit: 0,
    enabled: !disabledPages.includes("/replay"),
    icon: MdOutlineReplayCircleFilled,
  },
  "/imports": {
    name: "Imports",
    path: "/imports",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Imports",
    permissionBit: 0,
    enabled: !disabledPages.includes("/imports"),
    icon: FaDownload,
  },
  "/server": {
    name: "Server",
    path: "/server",
    showInNavBar: true,
    requiresPermission: true,
    permissionGroup: "Server",
    permissionBit: 0,
    enabled: !disabledPages.includes("/server"),
    icon: FaServer,
  },
  "/403": {
    name: "Not Authorized",
    path: "/403",
    showInNavBar: false,
    requiresPermission: false,
    permissionGroup: null,
    permissionBit: null,
    enabled: true,
  },
  "/404": {
    name: "Not Found",
    path: "*",
    showInNavBar: false,
    requiresPermission: false,
    permissionGroup: null,
    permissionBit: null,
    enabled: true,
  },
};

export const routeConfigs: Record<string, RouteConfig> = rawRouteConfigs;
